import React, { useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useQuery } from 'react-query';
import { useHistory } from 'react-router-dom';

import {
  Box,
  Grid,
  LinearProgress,
  Theme,
  Typography,
  createStyles,
  makeStyles,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';

import WhatsappDialog from '../../components/CommonDialogs/WhatsappDialog.component';
import Loader from '../../components/LoaderSplash/Loader.component';
import { useAuthDataContext } from '../../context/auth/auth.provider';
import {
  VEHICLES_QUERY,
  VEHICLES_QUERY_CONFIG,
  vehiclesQuery,
} from '../../query/vehicles/vehicles';
import { useProductNavigationStore } from '../../stores/Navigation/product-navigation.store';
import SubHeader from '../Header/SubHeader.component';
import VehicleCards from '../VehicleCards/VehicleCards.component';
import VehicleList from '../VehicleList/VehicleList.component';
import PolicyCard from './PolicyCard.component';

const useStyle = makeStyles((theme: Theme) =>
  createStyles({
    card: {
      height: `calc(100% - ${theme.spacing(2)}px)`,
      '& a': {
        height: '100%',
      },
    },
    endText: {
      textAlign: 'center',
    },
  })
);

interface PolicyList {
  header_title: any;
  section_from: string;
  policies: any;
  lastPageEmpty: any;
  fetchNextPage: any;
  onlyCarProduct: any;
  loading: boolean;
  policyId?: number;
  plateNumber?: string;
}

const PolicyList: React.FC<PolicyList> = ({
  header_title,
  section_from,
  policies,
  lastPageEmpty,
  fetchNextPage,
  onlyCarProduct,
  loading,
  policyId,
  plateNumber,
}) => {
  const theme = useTheme();
  const classes = useStyle();
  const matches = useMediaQuery(theme.breakpoints.down('sm'));

  const history = useHistory();

  const {
    data: vehicles,
    isFetching,
    isLoading,
  } = useQuery(
    [VEHICLES_QUERY, plateNumber],
    vehiclesQuery,
    VEHICLES_QUERY_CONFIG
  );

  const [showLoader, setShowLoader] = useState(true);

  useEffect(() => {
    if (!loading && !isLoading && !isFetching && !!!plateNumber) {
      setShowLoader(false); // margen de gracia
    }
  }, [loading, isLoading, isFetching, plateNumber]);

  const [pageNumber, setPageNumber] = useState(1);

  const handleNextPage = () => {
    fetchNextPage({ pageParam: pageNumber + 1 });
    setPageNumber((prev) => prev + 1);
  };

  const handleMore = () => {
    // Robustecer validación para detectar si hace falta cargar más registros o no.

    // Según lo consultado con BE, el paginado se realiza de a 20 registros por vez, por lo que para poder mostrar
    // un scroll infinito tendría que hacerce de a 20 registros por vez.
    const val = lastPageEmpty > 0 && policies.length >= 20 ? true : false;
    return val;
  };

  const lastPage = useProductNavigationStore((state) => state.lastPage);

  const setLastPageVisited = useProductNavigationStore(
    (state) => state.setLastPageVisited
  );

  const navigate_constants = useProductNavigationStore(
    (state) => state.constants
  );

  setLastPageVisited(navigate_constants.POLICY_LIST);

  const navigateBackFrom = useProductNavigationStore(
    (state) => state.navigateBackFrom
  );

  const { embed } = useAuthDataContext();
  const redirectHome = () => {
    embed
      ? window.parent.postMessage({ return: 'overview' }, '*')
      : history.push('/dashboard');
  };

  const goBack = () => {
    navigateBackFrom(navigate_constants.POLICY_LIST);
    if (!lastPage) redirectHome();
  };

  const [open, setOpenWhatsappDialog] = useState(false);

  return (
    <>
      <WhatsappDialog
        is_open={open}
        close_dialog={() => setOpenWhatsappDialog(false)}
        message={`En este momento no podemos mostrarte la información de tu póliza.
        Podés seguir operando vía Whatsapp.`}
      />

      <Box display={showLoader ? 'block' : 'none'}>
        <Loader action={'Cargando información'}></Loader>
      </Box>

      <InfiniteScroll
        //This is important field to render the next data
        dataLength={policies?.length}
        next={handleNextPage}
        hasMore={handleMore()}
        loader={
          <Box style={{ width: '100%' }}>
            <LinearProgress color="primary" />
          </Box>
        }
        endMessage={
          <Box m={2}>
            <Typography className={classes.endText} color="textSecondary">
              {' '}
              Ya hemos mostrado todos los resultados{' '}
            </Typography>
          </Box>
        }
        height={matches ? 600 : null}
      >
        <SubHeader
          content={
            <Box>
              <Typography variant="h6"> {header_title} </Typography>
            </Box>
          }
          goBack={() => goBack()}
        />

        {!onlyCarProduct && !plateNumber ? (
          <Grid container justify="center">
            <Grid item xs={11}>
              <Grid container>
                {policies?.length === 1
                  ? history.push(`/${section_from}/product/${policies[0]?.id}`)
                  : policies.map((policy: any, index: number) => (
                      <Grid item xs={12} sm={6} key={index}>
                        <Box mx={1} my={1} className={classes.card}>
                          <PolicyCard
                            policy={policy}
                            navigate={`/${section_from}/product/`}
                          />
                        </Box>
                      </Grid>
                    ))}
              </Grid>
            </Grid>
          </Grid>
        ) : onlyCarProduct && policyId && vehicles?.length > 1 ? (
          history.push(
            `/${section_from}/vehicle/${
              vehicles?.filter((v: any) => v.policy.id === policyId)[0]?.id
            }`
          )
        ) : vehicles?.length === 1 ? (
          history.push(`/${section_from}/vehicle/${vehicles[0].id}`)
        ) : vehicles?.length > 10 ? (
          <Grid container justify="center" alignItems="center">
            <Grid item xs={11}>
              <VehicleList
                policyId={policyId}
                baseUrl={`/${section_from}/vehicle/`}
                plateNumber={plateNumber}
              />
            </Grid>
          </Grid>
        ) : (
          <Grid container justify="center" alignItems="center">
            <Grid item xs={11}>
              <VehicleCards
                baseUrl={`/${section_from}/vehicle/`}
                policyId={policyId}
                verified={!!policyId}
                plateNumber={plateNumber}
              />
            </Grid>
          </Grid>
        )}
      </InfiniteScroll>
    </>
  );
};

export default PolicyList;
